import { rem } from 'polished'
import React, { useRef } from 'react'
import { CardCarousel } from 'components/CardCarousel'
import { ARROW_WITH_BACKGROUND_WRAPPER_HEIGHT } from 'components/CarouselNavigation'
import { CollectionWidgetCard } from 'components/CollectionWidgetCard'
import { Box, Container, Flex } from 'components/Layout'
import { ProductThumbnailCard } from 'components/ProductThumbnail'
import { H3 } from 'components/Typography'
import { ROUTE } from 'constants/routes'
import { withGA4Provider } from 'hoc/withGoogleAnalytics'
import {
  GA4_EVENTS,
  GA4_ITEM_LIST_NAME,
  useGoogleAnalytics,
} from 'providers/googleAnalytics'
import { useGAListItemView } from 'providers/googleAnalytics/utils/ListView/useGAListItemView'
import { FONT_SIZE, FONT_STACK, FONT_WEIGHT, SPACE } from 'Theme'
import { type PublicProductQuery } from 'types/graphql-generated'
import { getProductRoute } from 'utils/getProductRoute'
import { type TypesenseProductV5 } from 'utils/typesenseClient'
import { getProductSlug } from '../utils'

const CARD_WIDTH = { MOBILE: 'auto', TABLET: rem(260) } as const
const CAROUSEL_VERTICAL_PADDING = SPACE.PX_16
const SLIDES_PER_VIEW = { MOBILE: 2.3, DESKTOP: 'auto' } as const

interface MightAlsoLikeProductsCarouselBaseProps<T> {
  products: T[]
  reportDataConverter: (product: T, index: number) => any
  cardRenderer: (
    product: T,
    index: number
  ) => React.ComponentProps<typeof CardCarousel>['cards'][0]
}

type LegacyMightAlsoLikeProduct =
  PublicProductQuery['product']['mightAlsoLikeProducts'][0]

interface MightAlsoLikeProductsCarouselLegacyProps
  extends MightAlsoLikeProductsCarouselBaseProps<LegacyMightAlsoLikeProduct> {}

interface MightAlsoLikeProductsCarouselTypesenseProps
  extends MightAlsoLikeProductsCarouselBaseProps<TypesenseProductV5> {}

const MightAlsoLikeProductsCarouselContentBase = <T,>({
  products,
  cardRenderer,
  reportDataConverter,
}: React.PWC<MightAlsoLikeProductsCarouselBaseProps<T>>) => {
  const intersectionRef = useRef(null)
  const { executeDataToDataLayer } = useGoogleAnalytics()
  useGAListItemView({
    itemRef: intersectionRef,
    reportData: products.map(reportDataConverter),
    onViewEvent: (items) => {
      executeDataToDataLayer({
        event: GA4_EVENTS.VIEW_ITEM_LIST,
        overrideData: {
          items,
        },
      })
    },
  })

  if (!products?.length) {
    return null
  }

  return (
    <Box overflow="hidden" ref={intersectionRef}>
      <Container>
        <H3
          fontFamily={FONT_STACK.SERIF}
          fontWeight={FONT_WEIGHT.NORMAL}
          fontSize={{ MOBILE: FONT_SIZE.PX_25, TABLET: FONT_SIZE.PX_32 }}
        >
          You might also like
        </H3>
      </Container>
      <Box mb={SPACE.PX_32}>
        <CardCarousel
          py={CAROUSEL_VERTICAL_PADDING}
          slidesPerView={SLIDES_PER_VIEW}
          cards={products.map(cardRenderer)}
          arrowsOffsetTop={`calc(1rem + (${CARD_WIDTH.TABLET} / 2) - (${ARROW_WITH_BACKGROUND_WRAPPER_HEIGHT} / 2))`} // centers arrow according to image (1 rem in the beginning is top padding of swiper)
        />
      </Box>
    </Box>
  )
}

const MightAlsoLikeProductsCarouselLegacyContent: React.FC<
  React.PWC<Pick<MightAlsoLikeProductsCarouselLegacyProps, 'products'>>
> = ({ products }) => (
  <MightAlsoLikeProductsCarouselContentBase<LegacyMightAlsoLikeProduct>
    products={products}
    reportDataConverter={({ variants }, index) => ({
      ...variants?.[0],
      index,
    })}
    cardRenderer={(
      { id, title, images, slug, variants, brand, availability },
      index
    ) => {
      const leadImage = variants?.find((v) => v.leadImage)?.leadImage
      const productImage = leadImage ?? images?.[0]

      return {
        card: (
          <Flex height="100%" width={CARD_WIDTH}>
            <CollectionWidgetCard
              index={index}
              title={title}
              productImageUrl={productImage?.url}
              productImageAlt={productImage?.alt}
              variants={variants}
              url={ROUTE.SHOWROOM_PRODUCT_DETAIL({ slug })}
              availability={availability}
              brand={brand?.id}
              productId={id}
            />
          </Flex>
        ),
        key: id,
      }
    }}
  />
)

export const MightAlsoLikeProductsLegacyCarousel = withGA4Provider({
  item_list_name: GA4_ITEM_LIST_NAME.YOU_MIGHT_ALSO_LIKE,
})(MightAlsoLikeProductsCarouselLegacyContent)

const MightAlsoLikeProductsCarouselTypesenseContent: React.FC<
  React.PWC<Pick<MightAlsoLikeProductsCarouselTypesenseProps, 'products'>>
> = ({ products }) => (
  <MightAlsoLikeProductsCarouselContentBase<TypesenseProductV5>
    products={products}
    reportDataConverter={({ sku, title }, index) => ({
      __typename: 'ProductVariant',
      sku,
      title,
      index,
    })}
    cardRenderer={({
      id,
      title,
      imageAlt,
      imageUrl,
      minPrice,
      maxPrice,
      minTradePrice,
      maxTradePrice,
      minTradeDiscount,
      breakoutOptionCount,
      carouselVariants,
      legacyProductId,
      brand,
      availabilityOrder,
      legacyProductVariantId,
    }) => ({
      card: (
        <Flex height="100%" width={CARD_WIDTH}>
          <ProductThumbnailCard
            title={title}
            productImageAlt={imageAlt}
            productImageUrl={imageUrl}
            price={minPrice} // Temporary workaround
            tradePrice={minTradePrice}
            priceRange={{ min: minPrice, max: maxPrice }}
            tradePriceRange={{ min: minTradePrice, max: maxTradePrice }}
            tradeDiscount={minTradeDiscount}
            url={getProductRoute({
              slug: getProductSlug({
                id: legacyProductId,
                title,
              }),
            })}
            breakoutOptionCount={breakoutOptionCount}
            carouselImages={carouselVariants}
            onRefetch={() => {}}
            variantId={legacyProductVariantId}
            productId={legacyProductId}
            availabilityOrder={availabilityOrder}
            shouldDisplayBlackFridayBadge={false}
            brand={brand}
          />
        </Flex>
      ),
      key: id,
    })}
  />
)

export const MightAlsoLikeProductsTypesenseCarousel = withGA4Provider({
  item_list_name: GA4_ITEM_LIST_NAME.YOU_MIGHT_ALSO_LIKE,
})(MightAlsoLikeProductsCarouselTypesenseContent)
